import { colors as themeColors } from '../utils/theme';
import { Box, Grid, Paper, Typography, IconButton, Button, TextField, Divider, MenuItem, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { Add, Remove, Delete } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useMemo } from 'react';

const API_URL = 'http://192.168.1.248:3103/api';

const CartPage = ({ loggedInUser, companyData }) => {
    const { cartItems, itemOrder, updateQty, updateColor, updateSize, removeItem, total } = useCart();
    const navigate = useNavigate();

    const [colorMap, setColorMap] = useState({});
    const [sizeMap, setSizeMap] = useState({});
    const [imageMap, setImageMap] = useState({});
    const [carouselIndex, setCarouselIndex] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [colorRes, sizeRes] = await Promise.all([
                    axios.get(`${API_URL}/colormaster`),
                    axios.get(`${API_URL}/sizemaster`)
                ]);

                const colors = {};
                colorRes.data.forEach((c) => { colors[c.colorId] = { name: c.itemColor, code: c.colorCode }; });

                const sizes = {};
                sizeRes.data.forEach((s) => { sizes[s.sizeId] = { name: s.itemSize, code: s.sizeCode }; });

                setColorMap(colors);
                setSizeMap(sizes);
            } catch (err) {
                console.error('Failed to load color/size data', err);
            }
        };

        fetchData();
    }, []);

    const items = useMemo(() => {
        if (!Array.isArray(itemOrder)) return [];
        return itemOrder.map((variantKey) => {
            const item = cartItems[variantKey];
            return item ? { variantKey, ...item } : null;
        }).filter(Boolean);
    }, [itemOrder, cartItems]);

    useEffect(() => {
        async function fetchImages() {
            const map = {};
            await Promise.all(
                items.map(async ({ product }) => {
                    try {
                        const res = await axios.get(`${API_URL}/product/images/${product.Id}`);
                        if (Array.isArray(res.data) && res.data.length) {
                            const sorted = res.data.sort((a, b) => a.sequence - b.sequence)
                                .map((img) => ({
                                    url: `data:${img.mimeType};base64,${img.image}`,
                                    sequence: img.sequence,
                                }));
                            map[product.Id] = sorted;
                        } else {
                            map[product.Id] = [];
                        }
                    } catch (err) {
                        console.error(`Failed to fetch images for product ${product.Id}`, err);
                        map[product.Id] = [];
                    }
                })
            );
            setImageMap(map);
        }

        if (items.length) fetchImages();
    }, [items]);

    const colorOptions = Object.entries(colorMap).map(([id, val]) => ({ id: +id, ...val }));
    const sizeOptions = Object.entries(sizeMap).map(([id, val]) => ({ id: +id, ...val }));

    return (
        <Box p={2} maxWidth="1200px" mx="auto">
            <Box display="flex" alignItems="center" mb={3}>
                <IconButton onClick={() => navigate('/main/orderDetail')}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" sx={{ ml: 1 }}>Shopping Cart</Typography>
            </Box>

            {items.length === 0 ? (<Typography>Your cart is empty.</Typography>) : (
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        {items.map(({ variantKey, product, qty }, idx) => {
                            const images = imageMap[product.Id] || [];
                            const currentIndex = carouselIndex[product.Id] || 0;

                            return (
                                <Paper key={variantKey} elevation={3} sx={{ p: 2, mb: 2, borderRadius: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, bgcolor: themeColors.neutral.surfaceMuted }}>
                                    <Box sx={{ width: 120, height: 120, position: 'relative', flexShrink: 0, borderRadius: 2, overflow: 'hidden' }}>
                                        {images.map((img, i) => (
                                            <Box key={i} component="img" src={img.url} alt={`product-${product.Id}-${i}`}
                                                sx={{
                                                    position: 'absolute',
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    top: 0,
                                                    left: 0,
                                                    opacity: currentIndex === i ? 1 : 0,
                                                    transition: 'opacity 0.6s ease-in-out',
                                                    zIndex: currentIndex === i ? 2 : 1
                                                }}
                                            />
                                        ))}

                                        {/* Left arrow */}
                                        {images.length > 1 && (
                                            <IconButton size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCarouselIndex(prev => ({
                                                        ...prev,
                                                        [product.Id]: ((prev[product.Id] || 0) - 1 + images.length) % images.length
                                                    }));
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: 3,
                                                    transform: 'translateY(-50%)',
                                                    bgcolor: 'rgba(255,255,255,0.7)',
                                                    '&:hover': { bgcolor: 'white' },
                                                    zIndex: 3
                                                }}
                                            >
                                                <ArrowBackIosNewIcon fontSize="inherit" />
                                            </IconButton>
                                        )}

                                        {/* Right arrow */}
                                        {images.length > 1 && (
                                            <IconButton size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCarouselIndex(prev => ({
                                                        ...prev,
                                                        [product.Id]: ((prev[product.Id] || 0) + 1) % images.length
                                                    }));
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    right: 3,
                                                    transform: 'translateY(-50%)',
                                                    bgcolor: 'rgba(255,255,255,0.7)',
                                                    '&:hover': { bgcolor: 'white' },
                                                    zIndex: 3
                                                }}
                                            >
                                                <ArrowForwardIosIcon fontSize="inherit" />
                                            </IconButton>
                                        )}

                                        {/* Dots */}
                                        {images.length > 1 && (
                                            <Box display="flex" justifyContent="center" position="absolute" bottom={8} width="100%" zIndex={3}>
                                                {images.map((_, i) => (
                                                    <Box key={i}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setCarouselIndex(prev => ({ ...prev, [product.Id]: i }));
                                                        }}
                                                        sx={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            mx: 0.5,
                                                            bgcolor: currentIndex === i ? 'primary.main' : 'grey.400',
                                                            cursor: 'pointer'
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Product Info */}
                                    <Box flex={1}>
                                        <Typography variant="subtitle1" fontWeight={600} noWrap>{idx + 1}. {product.itemCode}</Typography>
                                        <Typography variant="body2" color="text.secondary" noWrap>{product.description}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}> Unit: {product.unit} | HSN: {product.HSN} </Typography>

                                        <Grid container spacing={1}>
                                            <Grid item xs={6}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography variant="caption">Size:</Typography>
                                                    <TextField select size="small" value={product.sizeId || ''} onChange={(e) => updateSize(variantKey, +e.target.value)} sx={{ minWidth: 120 }} >
                                                        {sizeOptions.map((opt) => (<MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>))}
                                                    </TextField>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={6}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography variant="caption">Color:</Typography>
                                                    <TextField select size="small" value={product.colorId || ''} onChange={(e) => updateColor(variantKey, +e.target.value)} sx={{ minWidth: 120 }} >
                                                        {colorOptions.map((opt) => (
                                                            <MenuItem key={opt.id} value={opt.id}>
                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                    <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: opt.code, border: `1px solid ${themeColors.neutral.muted}` }} />
                                                                    <Typography variant="body2">{opt.name}</Typography>
                                                                </Box>
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        <Table size="small" sx={{ mt: 1, border: `1px solid ${themeColors.neutral.border}` }}>
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ borderColor: themeColors.neutral.border, width: '30%' }}>
                                                        <Typography variant="caption">Qty:</Typography> {qty}
                                                    </TableCell>
                                                    <TableCell sx={{ borderColor: themeColors.neutral.border }}>
                                                        <Typography variant="caption">Rate:</Typography> ₹{product.saleRate} × {qty} = ₹{(product.saleRate * qty)}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ borderColor: themeColors.neutral.border }}>
                                                        <Typography variant="caption">GST%:</Typography> {product.TaxGST || 0}%
                                                    </TableCell>
                                                    <TableCell sx={{ borderColor: themeColors.neutral.border }}>
                                                        <Typography variant="caption">Taxable Amt:</Typography> ₹{((product.saleRate * qty * (product.TaxGST || 0)) / 100)}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>

                                        <Box mt={1.5} display="flex" alignItems="center">
                                            <IconButton size="small" onClick={() => updateQty(variantKey, Math.max(product.minOrderQty || 1, qty - 1))} disabled={qty <= (product.minOrderQty || 1)} >
                                                <Remove fontSize="small" />
                                            </IconButton>
                                            <TextField size="small" value={qty} onChange={(e) => updateQty(variantKey, Math.max(product.minOrderQty || 1, +e.target.value))} sx={{ width: 60, mx: 1 }} inputProps={{ style: { textAlign: 'center' } }} />
                                            <IconButton size="small" onClick={() => updateQty(variantKey, qty + 1)}>
                                                <Add fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between', minWidth: 80 }}>
                                        <IconButton color="error" onClick={() => removeItem(variantKey)}>
                                            <Delete />
                                        </IconButton>
                                        <Typography variant="subtitle1" fontWeight={600}> ₹{(product.saleRate * qty).toFixed(2)} </Typography>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Grid>

                    {/* Order Summary */}
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, borderRadius: 1 }}>
                            <Typography variant="h6" gutterBottom>Order Summary</Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography>Subtotal</Typography>
                                <Typography>₹{total.toFixed(2)}</Typography>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            <Button sx={{ backgroundColor: themeColors.primary.main }} variant="contained" size="large" fullWidth onClick={() => navigate('/main/checkout-page')}> Proceed to Checkout </Button>
                        </Paper>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default CartPage;